import { GenerateFarmTokenDto, StandaloneTokenResponseDto } from '@agroone/entities'
import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Observable, of, tap } from 'rxjs'
import { API_URL } from '../models'
import { Router } from '@angular/router'
import { requireDefined } from '../utils/require-defined.util'

@Injectable({
  providedIn: 'root',
})
export class StandaloneTokenService {
  private readonly STORAGE_KEY: string = 'standalone-token'

  private _apiUrl: string = inject(API_URL)
  private _router: Router = inject(Router)
  private _httpClient: HttpClient = inject(HttpClient)

  /**
   * Extract token from URL parameters and store it
   */
  extractTokenFromUrl(): string | null {
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search)
    const token: string | null = urlParams.get('token')

    if (token) {
      this.storeToken(token)
    }

    return token
  }

  /**
   * Store token in localStorage
   */
  storeToken(token: string): void {
    sessionStorage.setItem(this.STORAGE_KEY, token)
  }

  /**
   * Get current token
   */
  getCurrentToken(): string | null {
    return sessionStorage.getItem(this.STORAGE_KEY)
  }

  /**
   * Clear stored token
   */
  clearToken(): void {
    sessionStorage.removeItem(this.STORAGE_KEY)
  }

  /**
   * Generate standalone link with token
   * This endpoint is for demo only, in the future this will handle the generate of multiple tokens
   * @param generateTokenDto The DTO containing farm IDs, body, and sendLink flag
   * @param attachments Optional array of files to attach to the email
   */
  generateLink(generateTokenDto: GenerateFarmTokenDto, attachments?: File[]): Observable<void> {
    const formData: FormData = new FormData()

    // Append DTO fields
    generateTokenDto.farmIds.forEach((id) => {
      formData.append('farmIds', id.toString())
    })
    formData.append('body', generateTokenDto.body)
    formData.append('sendLink', generateTokenDto.sendLink.toString())

    // Append attachments if provided
    if (attachments?.length) {
      attachments.forEach((file) => {
        formData.append('attachments', file, file.name)
      })
    }

    return this._httpClient.post<void>(`${this._apiUrl}/standalone-tokens/generate`, formData)
  }

  getTokenInfos(): Observable<StandaloneTokenResponseDto> {
    return this._httpClient.get<StandaloneTokenResponseDto>(`${this._apiUrl}/standalone-tokens/infos`, {
      params: {
        token: requireDefined(this.getCurrentToken(), 'the standalone token'),
      },
    })
  }

  invalidateCurrentToken(): Observable<void> {
    if (!this.getCurrentToken()) {
      return of(void 0)
    }
    return this._httpClient
      .post<void>(`${this._apiUrl}/standalone-tokens/invalidate`, { token: this.getCurrentToken() })
      .pipe(
        tap(() => {
          this.clearToken()
          this._router.navigate([`/leave-application`])
        }),
      )
  }
}
